import {
  OrderMarketType,
  OrderOperationType,
  OrderSettlementConditionType,
  OrderTransactionVenueType,
} from 'api/types/entity';
import { DocumentsTableFeature } from 'features/DocumentsTableFeature';
import { getYesOrNoOptions, lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { ManagerAutocompleteFeature } from 'modules/order/order/feature';
import { Field, useFormContext } from 'modules/order/order/forms/orderForm';
import { useOrderContext } from 'modules/order/order/providers/OrderProvider';

import { OrderSection } from '../../OrderSection';

import { OrderOperations } from './OrderOperations';
import { OrderStatusesHistory } from './OrderStatusesHistory';

export const ExecutionHistorySection = () => {
  const { t } = useTranslation();
  const { order, brokerTimezoneOffset } = useOrderContext();

  const { values } = useFormContext();

  const isPurchaseOrSale =
    order.operationType === OrderOperationType.SecuritiesPurchase ||
    order.operationType === OrderOperationType.SecuritiesSale;

  const isCreditingOrWithdrawal =
    order.operationType === OrderOperationType.SecuritiesWithdrawal ||
    order.operationType === OrderOperationType.SecuritiesDeposit;

  return (
    <OrderSection
      afterFields={
        <>
          <DocumentsTableFeature
            name="adminDocuments"
            title={t(
              'order.sections.executionHistorySection.fields.adminDocuments.title'
            )}
          />
          <OrderStatusesHistory />
          <OrderOperations brokerTimezoneOffset={brokerTimezoneOffset} />
        </>
      }
      fields={lodash.compact([
        <Field.Number
          fullWidth
          label={t(
            'order.sections.executionHistorySection.fields.fmNumber.label'
          )}
          name="fmNumber"
          placeholder={t(
            'order.sections.executionHistorySection.fields.fmNumber.placeholder'
          )}
        />,
        isPurchaseOrSale && (
          <Field.Autocomplete
            fullWidth
            showRequiredMark
            label={t(
              'order.sections.executionHistorySection.fields.marketType.label'
            )}
            name="marketType"
            optionLabelKey="label"
            options={Object.values(OrderMarketType).map((type) => ({
              label: t(`order.marketType.${type}`),
              value: type,
            }))}
            optionValueKey="value"
            placeholder={t(
              'order.sections.executionHistorySection.fields.marketType.placeholder'
            )}
          />
        ),
        <ManagerAutocompleteFeature />,
        <Field.Autocomplete
          fullWidth
          label={t(
            'order.sections.executionHistorySection.fields.hasBrokerRecommendation.label'
          )}
          name="hasBrokerRecommendation"
          optionLabelKey="label"
          options={getYesOrNoOptions(t)}
          optionValueKey="value"
          placeholder={t(
            'order.sections.executionHistorySection.fields.hasBrokerRecommendation.label'
          )}
          variant="select"
        />,
        isPurchaseOrSale && (
          <Field.Autocomplete
            fullWidth
            showRequiredMark
            label={t(
              'order.sections.executionHistorySection.fields.transactionVenue.label'
            )}
            name="transactionVenue"
            optionLabelKey="label"
            options={Object.values(OrderTransactionVenueType).map((type) => ({
              label: t(
                `order.sections.executionHistorySection.fields.transactionVenue.options.${type}`
              ),
              value: type,
            }))}
            optionValueKey="value"
            placeholder={t(
              'order.sections.executionHistorySection.fields.transactionVenue.placeholder'
            )}
            variant="select"
          />
        ),
        isCreditingOrWithdrawal && (
          <Field.Autocomplete
            key={values.settlementCondition?.value}
            fullWidth
            label={t(
              'order.sections.executionHistorySection.fields.settlementCondition.label'
            )}
            name="settlementCondition"
            optionLabelKey="label"
            options={Object.values(OrderSettlementConditionType).map(
              (type) => ({
                label: t(
                  `order.sections.executionHistorySection.fields.settlementCondition.options.${type}`
                ),
                value: type,
              })
            )}
            optionValueKey="value"
            placeholder={t(
              'order.sections.executionHistorySection.fields.settlementCondition.placeholder'
            )}
          />
        ),
        <Field.Text
          multiline
          label={t(
            'order.sections.executionHistorySection.fields.comment.label'
          )}
          maxLength={2000}
          name="comment"
          placeholder={t(
            'order.sections.executionHistorySection.fields.comment.placeholder'
          )}
        />,
      ])}
      spacing={16}
      title={t('order.sections.executionHistorySection.title')}
    />
  );
};
